import { CheckCircle2, Circle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PermissionDenied } from '@/components/ui/permission-denied';
import type { ApiError } from '@/core/api';
import { EmbeddingKeyCard } from '@/features/knowledge_base';
import { useIntegrationStatus } from '../queries';

export function IntegrationsPage() {
  const { data, isPending, isError, error } = useIntegrationStatus();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground text-sm">
          Connection status for platform integrations. Most credentials are managed by your
          administrator; the AI embedding key below is set per organization.
        </p>
      </div>

      {/* Founder/Admin-only editable card (renders nothing for other roles). */}
      <EmbeddingKeyCard />

      {isError ? (
        (error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load integration status.'}
          </p>
        )
      ) : isPending ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {data.map((provider) => (
            <Card key={provider.key}>
              <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-base">{provider.label}</CardTitle>
                  <CardDescription>{provider.category}</CardDescription>
                </div>
                {provider.configured ? (
                  <Badge variant="secondary" className="gap-1">
                    <CheckCircle2 className="size-3.5" /> Connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground gap-1">
                    <Circle className="size-3.5" /> Not configured
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="text-muted-foreground text-sm">
                {provider.configured
                  ? 'Active and available across the workspace.'
                  : 'Add credentials in the deployment environment to enable.'}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
